/**
 * Sensitivity Bucket Formatting
 * Color-codes sensitivity analysis results based on payment shock percentage
 */

export type SensitivityBucket = {
  key: "VERY_LOW" | "LOW" | "MEDIUM" | "HIGH" | "CRITICAL" | "NA";
  labelHe: string;
  className: string;
  bgColor: string;
  textColor: string;
};

/**
 * Format sensitivity percentage into color-coded bucket
 * Buckets based on +1% interest shock as % of current payment:
 * - NA: Not applicable (FIXED tracks) - gray
 * - < 2.5%: VERY_LOW (dark green)
 * - 2.5-5%: LOW (light green)
 * - 5-10%: MEDIUM (yellow)
 * - 10-15%: HIGH (orange)
 * - >= 15%: CRITICAL (red)
 *
 * @param sensitivityPct - Sensitivity as percentage (e.g., 8.5 for 8.5%), or null/0 for NA
 * @returns Sensitivity bucket with Hebrew label and styling
 */
export function formatSensitivityBucket(sensitivityPct: number | null): SensitivityBucket {
  // NA case: no sensitivity exposure (e.g., FIXED tracks)
  if (sensitivityPct === null || sensitivityPct === 0) {
    return {
      key: "NA",
      labelHe: "לא רלוונטי",
      className: "bg-gray-100 text-gray-600 border-gray-300",
      bgColor: "#f3f4f6",
      textColor: "#6b7280",
    };
  }

  if (sensitivityPct < 2.5) {
    return {
      key: "VERY_LOW",
      labelHe: "נמוך",
      className: "bg-green-600 text-white border-green-700",
      bgColor: "#16a34a",
      textColor: "#ffffff",
    };
  }

  if (sensitivityPct < 5) {
    return {
      key: "LOW",
      labelHe: "נמוך",
      className: "bg-green-100 text-white border-green-300",
      bgColor: "#86efac",
      textColor: "#ffffff",
    };
  }

  if (sensitivityPct < 10) {
    return {
      key: "MEDIUM",
      labelHe: "בינוני",
      className: "bg-yellow-100 text-yellow-800 border-yellow-300",
      bgColor: "#fef9c3",
      textColor: "#854d0e",
    };
  }

  if (sensitivityPct < 15) {
    return {
      key: "HIGH",
      labelHe: "גבוה",
      className: "bg-orange-100 text-orange-800 border-orange-300",
      bgColor: "#ffedd5",
      textColor: "#9a3412",
    };
  }

  return {
    key: "CRITICAL",
    labelHe: "גבוה",
    className: "bg-red-800 text-white border-red-900",
    bgColor: "#dc2626",
    textColor: "#ffffff",
  };
}

/**
 * Format currency for Hebrew locale
 * @param amount - Amount to format
 * @returns Formatted string with ₪ symbol
 */
export function formatCurrencyHe(amount: number): string {
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format percentage with one decimal place
 * @param pct - Percentage value (e.g., 8.5 for 8.5%)
 * @returns Formatted string with % symbol
 */
export function formatPercentHe(pct: number): string {
  return new Intl.NumberFormat('he-IL', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(pct / 100);
}
